import { useState, useEffect } from "react";
import { Sparkles, Share2, RefreshCw, User, CalendarDays, Disc, Compass, LogIn, Coins } from "lucide-react";
import { toast } from "sonner";
import { getAiAnalysis, getCachedAiAnalysis } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

const Field = ({ icon, label, value }) => (
  <div className="rounded-2xl bg-white/70 border border-[#eadcff] p-3">
    <div className="flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-wide text-[#8a2be2]">
      {icon}
      {label}
    </div>
    <div className="mt-1 text-sm font-semibold text-[#2d2824]">{value || "Unknown"}</div>
  </div>
);

export const AiExpertCard = ({ tuneId, tuneName, abc }) => {
  const { user, login, openPricing, refresh } = useAuth();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  // an already-studied tune shows its analysis immediately, for free
  useEffect(() => {
    let alive = true;
    setData(null);
    getCachedAiAnalysis(tuneId)
      .then((res) => {
        if (alive && res?.analysis) setData(res.analysis);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [tuneId]);

  const load = async (regenerate = false) => {
    setLoading(true);
    try {
      const res = await getAiAnalysis(tuneId, regenerate);
      setData(res.analysis);
      refresh();
    } catch (e) {
      const code = e?.response?.status;
      if (code === 401) {
        toast.error("Please sign in to use the AI Expert");
        login();
      } else if (code === 402) {
        toast.error(e?.response?.data?.detail || "No analyses left — go Premium or buy a pack");
        openPricing();
      } else {
        toast.error("The expert could not be reached. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const callsLeft = Number(user?.calls_left || 0);
  const canGenerate = user && callsLeft >= 1;

  const share = async () => {
    if (!data) return;
    const text = `${tuneName} — Expert notes\n\nComposer: ${data.composer}\nDate: ${data.date}\nOrigin: ${data.origin}\nFirst recording: ${data.first_recording}\n\n${data.history}\n\nABC notation:\n${abc}\n\nvia Foinn.org · thesession.org`;
    try {
      if (navigator.share) {
        await navigator.share({ title: `${tuneName} — Expert notes`, text });
      } else {
        await navigator.clipboard.writeText(text);
        toast.success("Expert card copied to clipboard");
      }
    } catch (e) {
      /* user cancelled */
    }
  };

  return (
    <div className="ai-shimmer-border rounded-3xl p-5" data-testid="ai-expert-card">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-[#8a2be2] to-[#e11d48] text-white">
            <Sparkles className="h-5 w-5" />
          </span>
          <div>
            <h3 className="font-serif text-2xl font-bold leading-none text-[#2d2824]">
              Expert Analysis
            </h3>
            <p className="text-xs font-semibold text-[#6e655b]">
              AI musicologist · Irish, Scottish & folk traditions
            </p>
          </div>
        </div>
        {data && (
          <button
            onClick={share}
            className="flex items-center gap-1.5 rounded-full bg-[#2d2824] px-4 py-2 text-sm font-bold text-white transition-transform duration-200 hover:scale-105"
            data-testid="ai-share-button"
          >
            <Share2 className="h-4 w-4" /> Share
          </button>
        )}
      </div>

      {!data && !loading && (
        <div className="mt-4">
          {!user && (
            <button
              onClick={login}
              className="flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-[#8a2be2] to-[#e11d48] px-6 py-4 text-base font-extrabold text-white shadow-lg transition-transform duration-200 hover:scale-[1.02] active:scale-95"
              data-testid="ai-signin-button"
            >
              <LogIn className="h-5 w-5" /> Sign in to reveal the story
            </button>
          )}
          {user && callsLeft < 1 && (
            <button
              onClick={openPricing}
              className="flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-[#8a2be2] to-[#e11d48] px-6 py-4 text-base font-extrabold text-white shadow-lg transition-transform duration-200 hover:scale-[1.02] active:scale-95"
              data-testid="ai-topup-button"
            >
              <Coins className="h-5 w-5" /> Go Premium for more analyses
            </button>
          )}
          {canGenerate && (
            <button
              onClick={() => load(false)}
              className="w-full rounded-2xl bg-gradient-to-r from-[#8a2be2] to-[#e11d48] px-6 py-4 text-base font-extrabold text-white shadow-lg transition-transform duration-200 hover:scale-[1.02] active:scale-95"
              data-testid="ai-generate-button"
            >
              Reveal the story of this tune
            </button>
          )}
          <p className="mt-2 text-center text-xs font-semibold text-[#6e655b]">
            {user
              ? `${callsLeft} analys${callsLeft === 1 ? "is" : "es"} left · already-studied tunes are free`
              : "AI musicologist · sign in for 3 free analyses"}
          </p>
        </div>
      )}

      {loading && (
        <div className="mt-4 flex items-center gap-3 text-[#6e655b]" data-testid="ai-loading">
          <RefreshCw className="h-5 w-5 animate-spin text-[#8a2be2]" />
          <span className="font-semibold">Consulting the sources…</span>
        </div>
      )}

      {data && (
        <div className="mt-4 space-y-4 float-up">
          <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
            <Field icon={<User className="h-3 w-3" />} label="Composer" value={data.composer} />
            <Field icon={<CalendarDays className="h-3 w-3" />} label="Date" value={data.date} />
            <Field icon={<Compass className="h-3 w-3" />} label="Origin" value={data.origin} />
            <Field icon={<Disc className="h-3 w-3" />} label="First recording" value={data.first_recording} />
          </div>

          <div className="whitespace-pre-line rounded-2xl bg-white/70 border border-[#eadcff] p-4 text-[15px] leading-relaxed text-[#2d2824]" data-testid="ai-history">
            {data.history}
          </div>

          {data.notable_players?.length > 0 && (
            <div>
              <div className="text-[11px] font-extrabold uppercase tracking-wide text-[#8a2be2]">
                Notable players
              </div>
              <div className="mt-1 flex flex-wrap gap-2">
                {data.notable_players.map((p, i) => (
                  <span
                    key={i}
                    className="rounded-full bg-white px-3 py-1 text-xs font-bold text-[#2d2824] border border-[#eadcff]"
                  >
                    {p}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-[#6e655b]">
              Confidence: <span className="uppercase">{data.confidence}</span>
            </span>
            <button
              onClick={() => load(true)}
              className="flex items-center gap-1 text-xs font-bold text-[#8a2be2] hover:underline"
              data-testid="ai-regenerate"
            >
              <RefreshCw className="h-3 w-3" /> Regenerate
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
